import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePageSeo } from "@/hooks/usePageSeo";
import { ArrowRight, ArrowLeft, Truck, ShieldCheck, Lightning, Package } from "@phosphor-icons/react";

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus("loading");
    api.get(`/telecom/shop/product/${slug}`)
      .then(({ data }) => { setP(data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  const out = p && typeof p.stock === "number" && p.stock <= 0;

  usePageSeo(p ? {
    title: `${p.name} — Buy Online | iYeneh Connect`,
    description: (p.description || `Buy the ${p.name} from iYeneh Connect — pre-configured, plug & play and ready to use on your iYeneh line.`).slice(0, 300),
    canonicalPath: `/shop/${p.slug || slug}`,
    image: p.image || undefined,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: p.name,
      image: p.image ? [p.image] : undefined,
      description: p.description || `Pre-configured ${p.name} from iYeneh Connect.`,
      brand: { "@type": "Brand", name: "iYeneh" },
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: (p.price_cents / 100).toFixed(2),
        availability: out ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
        url: `https://iyeneh.com/shop/${p.slug || slug}`,
      },
    },
  } : { title: "Phone Device — iYeneh Connect", description: "Shop pre-configured phone devices at iYeneh Connect.", canonicalPath: `/shop/${slug}` });

  const buy = () => navigate(`/checkout?device=${p.id}`);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-9 w-9" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/shop" className="hidden text-sm font-semibold text-muted-foreground hover:text-foreground sm:block">Shop Devices</Link>
            {user
              ? <Link to="/app" className="rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground">My Account</Link>
              : <Link to="/register" className="rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground">Create Free Account</Link>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="product-detail-page">
        <nav aria-label="Breadcrumb" className="mb-5 text-sm text-muted-foreground">
          <Link to="/shop" className="hover:text-foreground">Shop Phone Devices</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">{p?.name || "Device"}</span>
        </nav>

        {status === "loading" && <p className="text-sm text-muted-foreground">Loading…</p>}

        {status === "notfound" && (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center" data-testid="product-notfound">
            <Package size={40} className="mx-auto text-muted-foreground/60" />
            <p className="mt-3 font-semibold">Device not found</p>
            <Link to="/shop" className="mt-3 inline-flex text-sm font-bold text-primary">Back to shop</Link>
          </div>
        )}

        {status === "ok" && p && (
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="overflow-hidden rounded-3xl border border-border bg-secondary">
              {p.image
                ? <img src={p.image} alt={p.name} className="w-full object-cover" />
                : <div className="flex aspect-square items-center justify-center text-muted-foreground/40"><Package size={64} /></div>}
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight">{p.name}</h1>
              <p className="mt-3 font-mono-metric text-4xl font-bold">{money(p.price_cents, "USD")}</p>
              {out && <span className="mt-2 inline-flex rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-semibold text-red-500">Out of stock</span>}
              {p.description && <p className="mt-4 leading-relaxed text-muted-foreground">{p.description}</p>}
              <button onClick={buy} disabled={busy || out} data-testid="pd-buy"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground disabled:opacity-60">
                {busy ? "Redirecting…" : "Buy now"} <ArrowRight size={16} weight="bold" />
              </button>
              <div className="mt-6 grid gap-2 text-sm">
                <span className="flex items-center gap-2 text-muted-foreground"><Truck size={16} className="text-primary" /> Ships pre-configured to your door</span>
                <span className="flex items-center gap-2 text-muted-foreground"><Lightning size={16} className="text-primary" /> Zero setup — plug in and talk</span>
                <span className="flex items-center gap-2 text-muted-foreground"><ShieldCheck size={16} className="text-primary" /> Secure encrypted checkout</span>
              </div>
              <Link to="/shop" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back to all devices</Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
